import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Observable, Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';

// Imports de Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

// Imports de API y Estado
import { Season, FilterOption } from '@solverml/api';
import { StatisticsFilters } from '../../controller/statistics-state.service';
import {MatButton, MatIconButton} from "@angular/material/button";

// Creamos un tipo local para el payload de filtros que emitirá esta barra
export interface StatisticsFilterPayload {
  seasonId: number | null;
  competitionIds: number[];
  gender: number | null;
  textSearch: string | null;
}

@Component({
  selector: 'app-statistics-filter-bar',
  templateUrl: './statistics-filter-bar.component.html',
  styleUrls: ['./statistics-filter-bar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButton,
    MatIconButton
  ]
})
export class StatisticsFilterBarComponent implements OnInit, OnDestroy, OnChanges {

  @Input() filters$: Observable<StatisticsFilters | null>;
  @Input() initialFilters: StatisticsFilterPayload | null = null;
  @Input() statsMode: 'actas' | 'ia' = 'actas';
  @Output() filtersChanged = new EventEmitter<StatisticsFilterPayload>();

  public filterForm: FormGroup;
  private destroy$ = new Subject<void>();
  private fb = inject(FormBuilder);

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      // En modo actas es obligatorio; en modo IA es opcional
      seasonId: [null],
      // Filtros opcionales
      competitionIds: [[]],
      gender: [null],
      textSearch: ['']
    });
    this.configureSeasonControlValidator();

    if (this.initialFilters) {
      this.filterForm.patchValue({
        seasonId: this.initialFilters.seasonId,
        competitionIds: this.initialFilters.competitionIds || [],
        gender: this.initialFilters.gender,
        textSearch: this.initialFilters.textSearch
      });
    }

    // Reactividad: Escuchamos cambios en el formulario
    this.filterForm.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(400), // Espera 400ms después de teclear
      distinctUntilChanged()
    ).subscribe(value => {
      this.filtersChanged.emit(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['statsMode'] && this.filterForm) {
      this.configureSeasonControlValidator();
    }
  }

  ngOnDestroy(): void {
    // Garantiza que el último valor escrito (especialmente textSearch) se persista
    // aunque la navegación ocurra antes del debounce de valueChanges.
    if (this.filterForm) {
      this.filtersChanged.emit(this.filterForm.getRawValue() as StatisticsFilterPayload);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  public clearTextSearch(): void {
    this.filterForm.get('textSearch')?.setValue('');
  }

  public clearCompetitions(event: Event): void {
    event.stopPropagation(); // Evita que se abra el select al hacer clic
    this.filterForm.get('competitionIds')?.setValue([]);
  }

  public clearAllFilters(): void {
    this.filterForm.reset();
    this.filtersChanged.emit(this.filterForm.value);
  }

  private configureSeasonControlValidator(): void {
    const seasonControl = this.filterForm.get('seasonId');
    if (!seasonControl) return;

    if (this.statsMode === 'actas') {
      seasonControl.setValidators([Validators.required]);
    } else {
      seasonControl.clearValidators();
    }
    seasonControl.updateValueAndValidity({ emitEvent: false });
  }
}
